/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-fallthrough */
import React, {
  useState, useEffect, useContext, useMemo,
} from 'react';
import {
  Box, Button, Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  Typography,
  TextField, InputAdornment, Avatar,
} from '@mui/material';
import PropTypes from 'prop-types';
import SearchIcon from '@mui/icons-material/Search';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import DropDown from '../../components/dropdown';
import { UserContext } from '../../contexts/User';
import useQueryParams from '../../hooks/useQueryParams';
import AddProjectModal from './AddProjectModal';
import DeleteModal from './ProjectDeleteModal';
import ProjectEditModal from './ProjectEditModal';
import DuplicateModal from './DuplicateProjectModal';
import archiveRestoreIcon from '../../images/archiveicon.svg';
import emptyBoxIcon from '../../images/empty-box.png';

const getAvatarColor = (projectName) => {
  const colors = [
    '#72713B',
    '#AB86B9',
    '#4D9062',
    '#165968',
    '#B2B299',
    '#A62138',
    '#6C5CE7',
    '#3D33F2',
    '#B2BF29',
    '#f51439ff',
    '#292355ff',
    '#0e01ffff',
  ];

  let hash = 0;
  for (let i = 0; i < projectName.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = projectName.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

function PanelAction({ handleClick, disabled = false }) {
  return (
    <Box alignSelf="center" marginLeft="auto" py="8px">
      <Button
        disabled={disabled}
        variant="contained"
        color="secondary"
        onClick={() => handleClick()}
        sx={{
          backgroundColor: '#53A551', color: 'white', textTransform: 'unset',
        }}
      >
        Add Project
      </Button>
    </Box>
  );
}

PanelAction.propTypes = {
  handleClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

function CustomConfirmModal({
  open, onClose, title, description,
  projectName, onConfirm, loading, confirmText, icon,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          padding: '32px',
          maxWidth: '451px',
        },
      }}
    >
      <Box>
        {/* Icon */}
        <Box sx={{
          backgroundColor: '#e3f2fd',
          borderRadius: '50%',
          p: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '40px',
          height: '40px',
          mb: 3,
        }}
        >
          {icon}
        </Box>

        {/* Title */}
        <Typography sx={{
          fontFamily: 'Inter !important',
          fontSize: '20px !important',
          fontWeight: '600 !important',
          lineHeight: '24px !important',
          color: '#0E1E40 !important',
          mb: 2,
        }}
        >
          {title}
        </Typography>

        {/* Description */}
        <Typography sx={{
          fontFamily: 'Inter !important',
          fontSize: '14px !important',
          fontWeight: '400 !important',
          lineHeight: '20px !important',
          color: '#293855 !important',
          mb: 2,
        }}
        >
          {description}
        </Typography>

        {/* Project Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Typography sx={{
            fontFamily: 'Inter !important',
            fontSize: '14px !important',
            fontWeight: '400 !important',
            lineHeight: '20px !important',
            color: '#757575 !important',
            mr: 1,
          }}
          >
            Project:
          </Typography>
          <Typography sx={{
            fontFamily: 'Inter !important',
            fontSize: '14px !important',
            fontWeight: '500 !important',
            lineHeight: '20px !important',
            color: '#0E1E40 !important',
          }}
          >
            {projectName}
          </Typography>
        </Box>

        {/* Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <Button
            onClick={onClose}
            disabled={loading}
            sx={{
              fontFamily: 'Inter !important',
              fontSize: '14px !important',
              fontWeight: '400 !important',
              padding: '12px 16px',
              borderRadius: '4px',
              border: '1px solid #E0E1E6',
              color: '#0E1E40 !important',
              textTransform: 'none !important',
              '&:hover': { backgroundColor: '#f5f5f5' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            variant="contained"
            disabled={loading}
            sx={{
              fontFamily: 'Inter !important',
              fontSize: '14px !important',
              fontWeight: '500 !important',
              padding: '12px 24px',
              borderRadius: '4px',
              backgroundColor: '#53A551 !important',
              textTransform: 'none !important',
              boxShadow: 'none !important',
              '&:hover': {
                backgroundColor: '#53A551 !important',
                filter: 'brightness(0.9)',
                boxShadow: 'none !important',
              },
            }}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : confirmText}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
CustomConfirmModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  projectName: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  confirmText: PropTypes.string,
  icon: PropTypes.node,
};

CustomConfirmModal.defaultProps = {
  projectName: '',
  loading: false,
  confirmText: 'Confirm',
  icon: null,
};

function Projects() {
  const {
    member, accessToken, redirect, updateTokens,
  } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [projectToArchive, setProjectToArchive] = useState(null);
  const [projectToRestore, setProjectToRestore] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);

  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [projectToDuplicate, setProjectToDuplicate] = useState(null);
  const [duplicateLoading, setDuplicateLoading] = useState(false);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };
  const isProjectArchived = (project) => project.isArchived === 1
  || project.isArchived === true || project.isArchived === '1';

  const getData = async () => {
    if (!member?.workspaceId) {
      console.error('No workspace ID available');
      return;
    }

    if (!accessToken) {
      console.error('No access token available');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await sendRequest({
        method: 'GET',
        url: API.WORKSPACE.getProjects(member.workspaceId),
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response && response.data) {
        if (Array.isArray(response.data)) {
          setProjects(response.data);
        } else if (response.data.data && Array.isArray(response.data.data)) {
          setProjects(response.data.data);
        } else {
          console.error('Unexpected response format:', response);
          setError('Unexpected response format');
          setProjects([]);
        }
      } else if (response && Array.isArray(response)) {
        setProjects(response);
      } else {
        setProjects([]);
      }
    } catch (e) {
      console.error('Error fetching projects:', e);
      setError(e.message || 'Failed to fetch projects');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (member?.workspaceId && accessToken) {
      getData();
    }
  }, [member?.workspaceId, accessToken]);

  const handleCreateProject = async (projectData) => {
    setCreateLoading(true);
    try {
      const { members, teams, ...basicProjectData } = projectData;

      const response = await sendRequest({
        method: 'POST',
        url: API.WORKSPACE.createProject(member.workspaceId),
        data: basicProjectData,
      });

      console.log(response);

      if (response?.success && response?.data) {
        if (members && (members.managers?.length > 0
           || members.members?.length > 0 || members.viewers?.length > 0)) {
          const projectId = response.data.id;
          console.log(response.data.id, projectId);

          const allSelectedMembers = new Set();
          members.managers?.forEach((member) => allSelectedMembers.add(member.id));
          members.members?.forEach((member) => allSelectedMembers.add(member.id));
          members.viewers?.forEach((member) => allSelectedMembers.add(member.id));

          if (allSelectedMembers.size > 0) {
            const projectMembers = Array.from(allSelectedMembers).map((memberId) => {
              const isManager = members.managers?.some((m) => m.id === memberId) || false;
              const isViewer = members.viewers?.some((m) => m.id === memberId) || false;

              return {
                memberId,
                isManager,
                isViewer,
              };
            });

            console.log('Adding members:', projectMembers);

            const membersResponse = await sendRequest({
              method: 'POST',
              url: API.WORKSPACE.addProjectMembers(member.workspaceId, projectId),
              data: { members: projectMembers },
            });

            if (!membersResponse.success) {
              console.error('Failed to add members:', membersResponse);
            }
          }
        }

        if (teams && teams.length > 0) {
          const projectTeams = teams.map((team) => ({
            teamId: team.id,
          }));

          const projectId = response.data.id;
          console.log('Adding teams:', projectTeams);

          const teamsResponse = await sendRequest({
            method: 'POST',
            url: API.WORKSPACE.addProjectTeams(member.workspaceId, projectId),
            data: { teams: projectTeams },
          });

          if (!teamsResponse.success) {
            console.error('Failed to add teams:', teamsResponse);
          }
        }

        getData();
        return true;
      }
      throw new Error('Failed to create project');
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditProject = async (updatedData) => {
    if (!projectToEdit) return;

    setEditLoading(true);
    try {
      const { members, teams, ...basicProjectData } = updatedData;

      const response = await sendRequest({
        method: 'PUT',
        url: API.WORKSPACE.updateProject(member.workspaceId, projectToEdit.id),
        data: basicProjectData,
      });

      if (response?.success || response?.data) {
        const projectId = projectToEdit.id;

        if (members) {
          const allSelectedMembers = new Set();
          members.managers?.forEach((member) => allSelectedMembers.add(member.id));
          members.members?.forEach((member) => allSelectedMembers.add(member.id));
          members.viewers?.forEach((member) => allSelectedMembers.add(member.id));

          if (allSelectedMembers.size > 0) {
            const projectMembers = Array.from(allSelectedMembers).map((memberId) => {
              const isManager = members.managers?.some((m) => m.id === memberId) || false;
              const isViewer = members.viewers?.some((m) => m.id === memberId) || false;
              return { memberId, isManager, isViewer };
            });

            await sendRequest({
              method: 'POST',
              url: API.WORKSPACE.addProjectMembers(member.workspaceId, projectId),
              headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
              },
              data: { members: projectMembers },
            });
          }
        }

        if (teams && Array.isArray(teams)) {
          const teamIds = teams.map((team) => team.id);

          await sendRequest({
            method: 'POST',
            url: API.WORKSPACE.addProjectTeams(member.workspaceId, projectId),
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            data: { teamIds },
          });
        }

        getData();
        setShowEditModal(false);
        setProjectToEdit(null);
        return true;
      }
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteProject = async () => {
    setDeleteLoading(true);
    try {
      const response = await sendRequest({
        method: 'DELETE',
        url: API.WORKSPACE.deleteProject(member.workspaceId, projectToDelete.id),
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response?.success || response?.status === 200) {
        getData();
        setShowDeleteModal(false);
        setProjectToDelete(null);
      } else {
        throw new Error('Failed to delete project');
      }
    } catch (error) {
      console.error('Error deleting project:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCloseDeleteModal = () => {
    if (!deleteLoading) {
      setShowDeleteModal(false);
      setProjectToDelete(null);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const filteredProjects = useMemo(() => {
    if (!projects || projects.length === 0) return [];

    return projects.filter((project) => {
      const isArchived = project.isArchived === 1 || project.isArchived === true || project.isArchived === '1';

      const matchesTab = activeTab === 0 ? !isArchived : isArchived;

      const matchesSearch = searchTerm.trim() === ''
        || (project.name && project.name.toLowerCase().includes(searchTerm.toLowerCase()));

      return matchesTab && matchesSearch;
    });
  }, [projects, activeTab, searchTerm]);

  const handleArchiveClick = (project) => {
    setProjectToArchive(project);
    setShowArchiveConfirm(true);
  };

  const handleRestoreClick = (project) => {
    setProjectToRestore(project);
    setShowRestoreConfirm(true);
  };

  const handleArchiveProject = async () => {
    if (!projectToArchive) return;

    setArchiveLoading(true);
    try {
      const requestData = { isArchived: true };

      const response = await sendRequest({
        method: 'PUT',
        url: API.WORKSPACE.updateProject(member.workspaceId, projectToArchive.id),
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        data: requestData,
      });

      if (response?.success) {
        await getData();
        setShowArchiveConfirm(false);
        setProjectToArchive(null);
      }
    } catch (error) {
      console.error('Error archiving project:', error);
    } finally {
      setArchiveLoading(false);
    }
  };

  const handleRestoreProject = async () => {
    if (!projectToRestore) return;

    setRestoreLoading(true);
    try {
      const requestData = { isArchived: false };

      const response = await sendRequest({
        method: 'PUT',
        url: API.WORKSPACE.updateProject(member.workspaceId, projectToRestore.id),
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        data: requestData,
      });

      if (response?.success) {
        await getData();
        setShowRestoreConfirm(false);
        setProjectToRestore(null);
      }
    } catch (error) {
      console.error('Error restoring project:', error);
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleDuplicateProject = async () => {
    if (!projectToDuplicate) return;

    setDuplicateLoading(true);
    try {
      const duplicateData = {
        name: `${projectToDuplicate.name} (Copy)`,
        description: projectToDuplicate.description || '',
        billable: projectToDuplicate.billable,
        activity: projectToDuplicate.activity,
        idleTime: projectToDuplicate.idleTime,
        client: projectToDuplicate.client || '',
        isArchived: false,
      };

      console.log('Duplicating project with data:', duplicateData);

      const response = await sendRequest({
        method: 'POST',
        url: API.WORKSPACE.createProject(member.workspaceId),
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        data: duplicateData,
      });

      console.log('Duplicate response:', response);

      const newProject = response?.data || response;

      if (newProject?.id) {
        const projectId = newProject.id;

        if (projectToDuplicate.projectMembers?.length > 0) {
          const projectMembers = projectToDuplicate.projectMembers.map((member) => ({
            memberId: member.memberId,
            isManager: member.isManager || false,
            isViewer: member.isViewer || false,
          }));

          await sendRequest({
            method: 'POST',
            url: API.WORKSPACE.addProjectMembers(member.workspaceId, projectId),
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            data: { members: projectMembers },
          });
        }

        if (projectToDuplicate.projectTeams?.length > 0) {
          const projectTeams = projectToDuplicate.projectTeams.map((team) => ({
            teamId: team.teamId,
          }));

          await sendRequest({
            method: 'POST',
            url: API.WORKSPACE.addProjectTeams(member.workspaceId, projectId),
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            data: { teams: projectTeams },
          });
        }

        await getData();
        setShowDuplicateModal(false);
        setProjectToDuplicate(null);
        return true;
      }
      console.error('Invalid project response:', response);
      throw new Error('Invalid response from server');
    } catch (error) {
      console.error('Error duplicating project:', error);
      throw error;
    } finally {
      setDuplicateLoading(false);
    }
  };

  const sortedProjects = [...filteredProjects].sort((a, b) => {
    if (!sortBy) return 0;

    let aValue; let
      bValue;

    switch (sortBy) {
      case 'Project Name':
        aValue = (a.name || '').toLowerCase();
        bValue = (b.name || '').toLowerCase();
        break;
      case 'Members':
        aValue = a.projectMembers?.length || 0;
        bValue = b.projectMembers?.length || 0;
        break;
      case 'Teams':
        aValue = a.projectTeams?.length || 0;
        bValue = b.projectTeams?.length || 0;
        break;
      case 'Todos':
        aValue = a.Todos || '';
        bValue = b.Todos || '';
        // eslint-disable-next-line
        if (!isNaN(aValue) && !isNaN(bValue)) {
          aValue = Number(aValue);
          bValue = Number(bValue);
        } else {
          aValue = aValue.toString().toLowerCase();
          bValue = bValue.toString().toLowerCase();
        }
        break;
      case 'Member Limits':
        aValue = a.MemberLimits;
        bValue = b.MemberLimits;
        if (!aValue || aValue === 'None') aValue = 0;
        if (!bValue || bValue === 'None') bValue = 0;
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
        break;
      default:
        aValue = a[sortBy] || '';
        bValue = b[sortBy] || '';
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    }
    const comparison = aValue.toString().localeCompare(bValue.toString());
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const queryParams = useQueryParams();
  const queryAccessToken = queryParams.get('accessToken');

  useEffect(() => {
    if (!accessToken && !queryAccessToken) {
      redirect('/workspaces');
    }
    if (queryAccessToken) {
      updateTokens({ accessToken: queryAccessToken });
    }
  }, []);

  useEffect(() => {
    if (accessToken && queryAccessToken) {
      redirect('/projects');
    }
  }, [accessToken, queryAccessToken, redirect]);

  const handleAddProject = () => {
    setShowCreateModal(true);
  };

  if (!accessToken || !member) {
    return <div>Loading...</div>;
  }

  return (
    <Box>
      {error && (
      <Box mb={2} p={2} bgcolor="error.light" color="error.contrastText" borderRadius={1}>
        Error:
        {' '}
        {error}
      </Box>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="project status tabs"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '16px',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#53A551',
            },
          }}
        >
          <Tab
            label={`Active (${projects.filter((p) => !isProjectArchived(p)).length})`}
            sx={{ '&.Mui-selected': { color: '#53A551' } }}
          />
          <Tab
            label={`Archived (${projects.filter((p) => isProjectArchived(p)).length})`}
            sx={{ '&.Mui-selected': { color: '#53A551' } }}
          />
        </Tabs>
      </Box>

      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
        gap: 2,
        pl: 2,
      }}
      >
        <Box>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, mb: 0.5,
          }}
          >
            <Typography sx={{
              fontWeight: '600 !important',
              fontSize: '16px !important',
              color: '#333 !important',
              margin: '0 !important',
            }}
            >
              Projects
            </Typography>
          </Box>
          <Typography sx={{
            color: '#666 !important',
            fontSize: '14px !important',
            margin: '0 !important',
          }}
          >
            Create and manage projects to streamline team collaboration and tracking.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            placeholder="Search projects"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{
              width: '250px',
              '& .MuiOutlinedInput-root': {
                fontSize: '14px !important',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#999', fontSize: '18px' }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            onClick={handleAddProject}
            disabled={loading}
            sx={{
              backgroundColor: '#53A551 !important',
              color: 'white !important',
              textTransform: 'none !important',
              fontSize: '14px !important',
              fontWeight: '500 !important',
              padding: '8px 16px !important',
              whiteSpace: 'nowrap',
              '&:hover': {
                backgroundColor: '#45a049 !important',
              },
            }}
          >
            Add Project
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              {['Project Name', 'Members', 'Teams', 'Todos', 'Member Limits'].map((header) => (
                <TableCell
                  key={header}
                  onClick={() => handleSort(header)}
                  sx={{
                    fontWeight: '600 !important',
                    fontSize: '14px !important',
                    color: '#333 !important',
                    cursor: 'pointer',
                  }}
                >
                  {header}
                  <span style={{ float: 'right', marginLeft: '8px' }}>
                    {/* eslint-disable-next-line */}
                    {sortBy === header ? (sortOrder === 'asc' ? '↑' : '↓') : '↕'}
                  </span>
                </TableCell>
              ))}
              <TableCell sx={{
                fontWeight: '600 !important',
                fontSize: '14px !important',
                color: '#333 !important',
              }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedProjects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 8, border: 'none' }}>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    color: '#999',
                  }}
                  >
                    <Box sx={{
                      width: '80px', height: '80px', mb: 2, opacity: 0.5,
                    }}
                    >
                      <img
                        src={emptyBoxIcon}
                        alt="No projects"
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: '14px', color: '#999', fontStyle: 'italic' }}>
                      No projects were found. Please add one!
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              sortedProjects.map((project, index) => {
                let options = [];
                if (activeTab === 0) {
                  options = [
                    {
                      text: 'Edit Project',
                      handleClick: () => {
                        setTimeout(() => {
                          setProjectToEdit(project);
                          setShowEditModal(true);
                        }, 0);
                      },
                    },
                    {
                      text: 'Duplicate Project',
                      handleClick: () => {
                        setTimeout(() => {
                          setProjectToDuplicate(project);
                          setShowDuplicateModal(true);
                        }, 0);
                      },
                    },
                    {
                      text: 'Archive Project',
                      handleClick: () => {
                        setTimeout(() => {
                          handleArchiveClick(project);
                        }, 0);
                      },
                    },
                    {
                      text: 'Delete Project',
                      handleClick: () => {
                        setTimeout(() => {
                          setProjectToDelete(project);
                          setShowDeleteModal(true);
                        }, 0);
                      },
                      style: { color: '#CC4525' },
                    },
                  ];
                } else {
                  options = [
                    {
                      text: 'Restore Project',
                      handleClick: () => {
                        setTimeout(() => {
                          handleRestoreClick(project);
                        }, 0);
                      },
                    },
                    {
                      text: 'Delete Project',
                      handleClick: () => {
                        setTimeout(() => {
                          setProjectToDelete(project);
                          setShowDeleteModal(true);
                        }, 0);
                      },
                      style: { color: '#CC4525' },
                    },
                  ];
                }

                return (
                  <TableRow key={project.id || index} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            backgroundColor: getAvatarColor(project.name || 'Untitled'),
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: 600,
                          }}
                        >
                          {(project.name || 'U').charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {project.name || 'Untitled Project'}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ fontSize: '14px' }}>
                      {project.projectMembers?.length || 0}
                    </TableCell>

                    <TableCell sx={{ fontSize: '14px' }}>
                      {project.projectTeams?.length || 0}
                    </TableCell>

                    <TableCell sx={{ fontSize: '14px' }}>
                      {project.Todos || 'No Todos'}
                    </TableCell>

                    <TableCell sx={{ fontSize: '14px' }}>
                      {project.MemberLimits || 'None'}
                    </TableCell>

                    <TableCell>
                      <DropDown title="Actions" options={options} />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <AddProjectModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateProject}
        loading={createLoading}
        title="New Project"
      />

      <ProjectEditModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setProjectToEdit(null);
        }}
        onSubmit={handleEditProject}
        loading={editLoading}
        projectData={projectToEdit}
      />

      <DeleteModal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteProject}
        loading={deleteLoading}
        projectName={projectToDelete?.name}
      />

      <DuplicateModal
        open={showDuplicateModal}
        onClose={() => !duplicateLoading && setShowDuplicateModal(false)}
        onConfirm={handleDuplicateProject}
        loading={duplicateLoading}
        projectName={projectToDuplicate?.name}
      />

      <CustomConfirmModal
        open={showArchiveConfirm}
        onClose={() => !archiveLoading && setShowArchiveConfirm(false)}
        title="Are you sure to archive this project?"
        description="The project will be archived, but the ongoing sessions in the project will go on till end."
        projectName={projectToArchive?.name}
        onConfirm={handleArchiveProject}
        loading={archiveLoading}
        confirmText="Archive"
        confirmColor="#53A551"
        icon={(
          <img
            src={archiveRestoreIcon}
            alt="Archive Icon"
            width="48"
            height="48"
          />
          )}
      />

      <CustomConfirmModal
        open={showRestoreConfirm}
        onClose={() => !restoreLoading && setShowRestoreConfirm(false)}
        title="Are you sure to restore this project?"
        description="The project will be restored and you can use this policy again for selected teams and members"
        projectName={projectToRestore?.name}
        onConfirm={handleRestoreProject}
        loading={restoreLoading}
        confirmText="Restore"
        confirmColor="#53A551"
        icon={(
          <img
            src={archiveRestoreIcon}
            alt="Restore Icon"
            width="48"
            height="48"
          />
          )}
      />
    </Box>
  );
}

export default Projects;
